import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Form, Input, Select } from 'antd';
import JornadasList from 'componentsv2/Jornadas';
import {
  getGroupMatchDays,
  getMatchIncidentsCards,
  getMatchIncidentsExpulsions,
  getMatchIncidentsFaults,
  getMatchIncidentsGoals,
} from 'api/events';
import { useParams } from 'react-router-dom';
import BlockPageLoader from 'components/Loaders/BlockPageLoader';
import RegisterCard from 'componentsv2/RegisterCard';

const JornadasFutbol = () => {
  let { eventId, matchDayId, groupId, matchId } = useParams();

  const [rounds, setRounds] = useState([]);
  const [goalIncidents, setGoalIncidents] = useState([]);
  const [faultIncidents, setFaultIncidents] = useState([]);
  const [cardIncidents, setCardIncidents] = useState([]);
  const [expulsionIncidents, setExpulsionIncidents] = useState([]);
  const [loadingGroupRounds, setLoadingGroupRounds] = useState(true);
  const [loadingGoalIncidents, setLoadingGoalIncidents] = useState(true);
  const [loadingFaultIncidents, setLoadingFaultIncidents] = useState(true);
  const [loadingCardIncidents, setLoadingCardIncidents] = useState(true);
  const [loadingExpulsionIncidents, setLoadingExpulsionIncidents] = useState(true);

  const roundMatches = useMemo(
    () =>
      rounds.reduce((roundsAccumulate, round) => {
        return [
          ...roundsAccumulate,
          ...round.matches.map((roundMatch) => ({
            ...roundMatch,
            round_id: round.id,
          })),
        ];
      }, []),
    [rounds]
  );
  const roundMatch = useMemo(
    () =>
      roundMatches.filter((rounMatch) => rounMatch.id === Number(matchId))[0],
    [matchId, roundMatches]
  );
  const teams = useMemo(() => (roundMatch ? [roundMatch?.home, roundMatch?.visit] : []),
  [roundMatch]);

  const teamsDropdownOptions = useMemo(
    () =>
      teams.map((team) => ({
        value: String(team.id),
        label: team.name,
      })),
    [teams]
  );

  const onLoadRounds = useCallback(() => {
    setLoadingGroupRounds(true);

    getGroupMatchDays(groupId)
      .then(({ body, status }) => {
        if ([200, 201].includes(status)) {
          setRounds(body?.data?.rounds || []);
        }
        setLoadingGroupRounds(false);
      })
      .catch(() => {
        setLoadingGroupRounds(false);
      });
  }, [groupId]);

  // callbacks
  const loadGoalIncidents = useCallback(() => {
    setLoadingGoalIncidents(true);

    getMatchIncidentsGoals(matchId)
      .then(({ status, body }) => {
        if ([200].includes(status)) {
          setGoalIncidents(body?.data?.incidents || []);
        }

        setLoadingGoalIncidents(false);
      })
      .catch(() => {
        setLoadingGoalIncidents(false);
      });
  }, [matchId]);

  const loadFaultIncidents = useCallback(() => {
    setLoadingFaultIncidents(true);

    getMatchIncidentsFaults(matchId)
      .then(({ status, body }) => {
        if ([200].includes(status)) {
          setFaultIncidents(body?.data?.incidents || []);
        }

        setLoadingFaultIncidents(false);
      })
      .catch(() => {
        setLoadingFaultIncidents(false);
      });
  }, [matchId]);

  const loadCardIncidents = useCallback(() => {
    setLoadingCardIncidents(true);

    getMatchIncidentsCards(matchId)
      .then(({ status, body }) => {
        if ([200].includes(status)) {
          setCardIncidents(body?.data?.incidents || []);
        }

        setLoadingCardIncidents(false);
      })
      .catch(() => {
        setLoadingCardIncidents(false);
      });
  }, [matchId]);

  const loadExpulsionIncidents = useCallback(() => {
    setLoadingExpulsionIncidents(true);

    getMatchIncidentsExpulsions(matchId)
      .then(({ status, body }) => {
        if ([200].includes(status)) {
          setExpulsionIncidents(body?.data?.incidents || []);
        }

        setLoadingExpulsionIncidents(false);
      })
      .catch(() => {
        setLoadingExpulsionIncidents(false);
      });
  }, [matchId]);

  const loadIncidents = useCallback(() => {
    loadGoalIncidents();
    loadFaultIncidents();
    loadCardIncidents();
    loadExpulsionIncidents();
  }, [loadCardIncidents, loadExpulsionIncidents, loadFaultIncidents, loadGoalIncidents]);

  useEffect(() => {
    onLoadRounds();
    loadIncidents();
  }, [groupId, onLoadRounds, loadIncidents]);

  const uniqueRound = rounds?.find(round => {
    return round?.id === Number(matchDayId);
  });

  const match = uniqueRound?.matches?.find(match => match?.id === Number(matchId));

  if (loadingGroupRounds) {
    return <BlockPageLoader className="min-h-screen" valignTop />;
  }

  return (
    <>
      <Card title={'Editar Encuentro'}>
        <JornadasList key={match?.id} match={match} isEdit={true} displayOnly={true} />
        <div
          style={{
            marginTop: '34px',
            marginBottom: '34px',
            fontSize: '26px',
            display: 'flex',
            justifyContent: 'center',
          }}
          className="avatar-status-name"
        >
          Estadisticas Individuales
        </div>
        <Row gutter={[16, 0]} justify={'space-between'}>
          <Col flex xs={24} sm={24} md={12} lg={12} xl={12}>
            <RegisterCard
              data={goalIncidents}
              teamList={teamsDropdownOptions}
              incidentType="goal"
              title={'Registro de Goles'}
              reloadStats={loadGoalIncidents}
            >
              <Form.Item
                label="Goles anotados"
                name="goal_amount"
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el número de goles anotados!',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </RegisterCard>
          </Col>
          <Col flex xs={24} sm={24} md={12} lg={12} xl={12}>
            <RegisterCard
              data={faultIncidents}
              teamList={teamsDropdownOptions}
              incidentType="fault"
              title={'Registro de Faltas'}
              reloadStats={loadFaultIncidents}
            />
          </Col>
          <Col flex xs={24} sm={24} md={12} lg={12} xl={12}>
            <RegisterCard
              data={expulsionIncidents}
              teamList={teamsDropdownOptions}
              incidentType="expulsion"
              title={'Expulsados'}
              reloadStats={loadExpulsionIncidents}
            />
          </Col>
          <Col flex xs={24} sm={24} md={12} lg={12} xl={12}>
            <RegisterCard
              data={cardIncidents}
              teamList={teamsDropdownOptions}
              incidentType="card"
              title={'Tarjetas'}
              reloadStats={loadCardIncidents}
            >
              <Form.Item
                label="Tarjeta"
                name="card_color"
                rules={[
                  {
                    required: true,
                    message: 'Ingrese la tarjeta!',
                  },
                ]}
              >
                <Select
                  options={[
                    {
                      label: 'Amarilla',
                      value: 'yellow',
                    },
                    {
                      label: 'Roja',
                      value: 'red',
                    },
                  ]}
                />
              </Form.Item>
            </RegisterCard>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default JornadasFutbol;
